import {
  InventoryOpsPoDetail,
  InventoryOpsReceivedSkuVariant,
  InventoryOpsRejectedSkuVariant,
  InventoryOpsSentSkuVariant,
  InventoryOpsSizeMix,
} from "../../../../../../models/ProjectManagement/ProjectInventoryProduct";
import { getPassedQtyBySku, getQcSnapshot } from "./inventoryOpsQc";

export type InventoryOpsPoTaskKey =
  | "raw_inward"
  | "raw_inward_qc"
  | "print_outward"
  | "print_inward"
  | "print_inward_qc";

const normalizeSku = (sku: string) => sku.trim().toLowerCase();

const toQty = (...values: Array<number | null | undefined>): number => {
  for (const value of values) {
    if (value != null && !Number.isNaN(Number(value))) {
      return Number(value);
    }
  }
  return 0;
};

const getQtyBySku = <T extends { sku: string }>(
  variants: T[] | null | undefined,
  getQty: (variant: T) => number,
): Map<string, number> => {
  const map = new Map<string, number>();
  (variants || []).forEach((variant) => {
    if (!variant.sku) return;
    map.set(normalizeSku(variant.sku), getQty(variant));
  });
  return map;
};

const getReceivedQty = (variant: InventoryOpsReceivedSkuVariant) =>
  toQty(variant.total_received_quantity, variant.quantity_received);

const getRejectedQty = (
  variant: InventoryOpsRejectedSkuVariant & { quantity?: number },
) =>
  toQty(
    variant.total_rejected_quantity,
    variant.rejected_quantity,
    variant.quantity,
  );

const getSentQty = (variant: InventoryOpsSentSkuVariant) =>
  toQty(variant.total_quantity_sent, variant.quantity_sent);

/** Net good qty for a variant = max(0, received − rejected). */
export const getNetInwardQty = (
  receivedQty: number,
  rejectedQty: number,
): number => Math.max(0, receivedQty - rejectedQty);

/**
 * True when every ordered SKU has effective raw coverage >= ordered qty.
 * Effective = (raw received − raw rejected) − print rejected (when customised).
 * Print rejects must be replaced by fresh raw inward through the full flow.
 * Falls back to aggregate PO qty when size mixes are unavailable.
 */
const isNetInwardCompleteAgainstOrdered = (
  po: InventoryOpsPoDetail,
  sizeMixes: InventoryOpsSizeMix[] | null | undefined,
  receivedVariants: InventoryOpsReceivedSkuVariant[] | null | undefined,
  rejectedVariants:
    | Array<InventoryOpsRejectedSkuVariant & { quantity?: number }>
    | null
    | undefined,
  aggregateReceived: number,
  aggregateRejected: number,
  options?: {
    customisationRequired?: boolean;
    printRejectedVariants?: Array<
      InventoryOpsRejectedSkuVariant & { quantity?: number }
    > | null;
    aggregatePrintRejected?: number;
  },
): boolean => {
  const customisationRequired = options?.customisationRequired !== false;
  const orderedMixes = (sizeMixes || []).filter(
    (mix) => Boolean(mix.sku) && (mix.quantity ?? 0) > 0,
  );

  if (orderedMixes.length > 0) {
    const receivedBySku = getQtyBySku(receivedVariants, getReceivedQty);
    const rejectedBySku = getQtyBySku(rejectedVariants, getRejectedQty);
    const printRejectedBySku = customisationRequired
      ? getQtyBySku(options?.printRejectedVariants, getRejectedQty)
      : new Map<string, number>();

    return orderedMixes.every((mix) => {
      const sku = normalizeSku(mix.sku);
      const rawNet = getNetInwardQty(
        receivedBySku.get(sku) ?? 0,
        rejectedBySku.get(sku) ?? 0,
      );
      const printRejected = printRejectedBySku.get(sku) ?? 0;
      const effective = Math.max(0, rawNet - printRejected);
      return effective >= (mix.quantity ?? 0);
    });
  }

  const poQty = po.product_po_quantity ?? 0;
  if (poQty <= 0) return false;
  const rawNet = getNetInwardQty(aggregateReceived, aggregateRejected);
  const printRejected = customisationRequired
    ? (options?.aggregatePrintRejected ?? 0)
    : 0;
  return Math.max(0, rawNet - printRejected) >= poQty;
};

const toTimestamp = (value?: string | Date | null): number | null => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

/** Latest modified/created timestamp per SKU from variant rows. */
const getLatestTimestampBySku = <
  T extends {
    sku: string;
    modified_date?: string | Date | null;
    created_date?: string | Date | null;
  },
>(
  variants: T[] | null | undefined,
): Map<string, number> => {
  const map = new Map<string, number>();
  (variants || []).forEach((variant) => {
    if (!variant.sku) return;
    const sku = normalizeSku(variant.sku);
    const ts =
      toTimestamp(variant.modified_date) ?? toTimestamp(variant.created_date);
    if (ts == null) return;
    const existing = map.get(sku);
    if (existing == null || ts > existing) {
      map.set(sku, ts);
    }
  });
  return map;
};

/**
 * Every inwarded SKU must have a QC record that is not older than the inward.
 * Example: inward at 2:30 and last QC at 2:29 → QC still pending for that inward.
 *
 * Product-level (no SKU rows): same freshness rule using aggregate timestamps.
 * Example: print reject reopens raw inward → after +N raw, 1st QC must be redone.
 */
const isQcSavedForAllInwardedVariants = (
  receivedVariants: InventoryOpsReceivedSkuVariant[] | null | undefined,
  qcVariants:
    | Array<InventoryOpsRejectedSkuVariant & { quantity?: number }>
    | null
    | undefined,
  aggregateReceived: number,
  aggregateRejectionRecorded: boolean,
  options?: {
    aggregateInwardModifiedAt?: string | Date | null;
    aggregateQcModifiedAt?: string | Date | null;
  },
): boolean => {
  const receivedBySku = getQtyBySku(receivedVariants, getReceivedQty);
  const inwardedSkus = Array.from(receivedBySku.entries()).filter(
    ([, qty]) => qty > 0,
  );

  if (inwardedSkus.length > 0) {
    const qcSkus = new Set(
      (qcVariants || [])
        .filter((variant) => Boolean(variant.sku))
        .map((variant) => normalizeSku(variant.sku)),
    );
    const inwardTsBySku = getLatestTimestampBySku(receivedVariants);
    const qcTsBySku = getLatestTimestampBySku(qcVariants);

    return inwardedSkus.every(([sku]) => {
      if (!qcSkus.has(sku)) return false;

      const inwardTs = inwardTsBySku.get(sku);
      // No inward timestamp from API → fall back to QC presence only.
      if (inwardTs == null) return true;

      const qcTs = qcTsBySku.get(sku);
      // Inward is newer than last QC (or QC has no timestamp) → pending.
      if (qcTs == null) return false;
      return qcTs >= inwardTs;
    });
  }

  // Aggregate / non-SKU mode.
  if (aggregateReceived <= 0) return false;
  if (!aggregateRejectionRecorded) return false;

  const inwardTs = toTimestamp(options?.aggregateInwardModifiedAt);
  const qcTs = toTimestamp(options?.aggregateQcModifiedAt);
  // Missing timestamps → keep prior behavior (QC presence only).
  if (inwardTs == null || qcTs == null) return true;
  return qcTs >= inwardTs;
};

/**
 * QC complete only when:
 * 1. (inward qty − rejected qty) >= PO product qty
 * 2. Tracked QC (passed qty recorded): nothing is pending.
 *    Legacy QC: QC is saved for every inwarded variant, and that QC is not
 *    older than the inward.
 */
const isInwardQcComplete = (
  po: InventoryOpsPoDetail,
  sizeMixes: InventoryOpsSizeMix[] | null | undefined,
  receivedVariants: InventoryOpsReceivedSkuVariant[] | null | undefined,
  qcVariants:
    | Array<InventoryOpsRejectedSkuVariant & { quantity?: number }>
    | null
    | undefined,
  aggregateReceived: number,
  aggregateRejected: number | null | undefined,
  options?: {
    aggregateInwardModifiedAt?: string | Date | null;
    aggregateQcModifiedAt?: string | Date | null;
    /** Pending qty when QC is tracked; null/undefined = legacy rules. */
    trackedPendingQty?: number | null;
  },
): boolean => {
  const poQty = po.product_po_quantity ?? 0;
  if (poQty <= 0) return false;

  const receivedBySku = getQtyBySku(receivedVariants, getReceivedQty);
  const rejectedBySku = getQtyBySku(qcVariants, getRejectedQty);
  const orderedMixes = (sizeMixes || []).filter(
    (mix) => Boolean(mix.sku) && (mix.quantity ?? 0) > 0,
  );

  let netGoodQty = 0;
  if (receivedBySku.size > 0 || orderedMixes.length > 0) {
    const skus = new Set<string>([
      ...orderedMixes.map((mix) => normalizeSku(mix.sku)),
      ...Array.from(receivedBySku.keys()),
    ]);
    skus.forEach((sku) => {
      netGoodQty += getNetInwardQty(
        receivedBySku.get(sku) ?? 0,
        rejectedBySku.get(sku) ?? 0,
      );
    });
  } else {
    netGoodQty = getNetInwardQty(aggregateReceived, aggregateRejected ?? 0);
  }

  if (netGoodQty < poQty) return false;

  if (options?.trackedPendingQty != null) {
    return options.trackedPendingQty <= 0;
  }

  return isQcSavedForAllInwardedVariants(
    receivedVariants,
    qcVariants,
    aggregateReceived,
    aggregateRejected != null,
    options,
  );
};

const isTimestampFreshOrUnknown = (
  laterTs: number | null | undefined,
  earlierTs: number | null | undefined,
): boolean => {
  // Missing timestamps → don't block (backward compatible with older API payloads).
  if (earlierTs == null || laterTs == null) return true;
  return laterTs >= earlierTs;
};

/** Print outward complete when every fresh QC-approved SKU is fully sent. */
const isPrintOutwardComplete = (
  po: InventoryOpsPoDetail,
  sizeMixes: InventoryOpsSizeMix[] | null | undefined,
): boolean => {
  const orderedMixes = (sizeMixes || []).filter(
    (mix) => Boolean(mix.sku) && (mix.quantity ?? 0) > 0,
  );
  const receivedBySku = getQtyBySku(po.raw_inward_sku_variants, getReceivedQty);
  const rejectedBySku = getQtyBySku(
    po.raw_inward_qc_sku_variants,
    getRejectedQty,
  );
  const sentBySku = getQtyBySku(po.print_outward_sku_variants, getSentQty);
  const passedBySku = getPassedQtyBySku(po.raw_inward_qc_sku_variants);
  const rawQc = getQcSnapshot(po, "raw");
  // Unchecked raw stock still has to be QC'd and sent.
  if (rawQc.tracked && (rawQc.pending ?? 0) > 0) return false;
  const inwardTsBySku = getLatestTimestampBySku(po.raw_inward_sku_variants);
  const qcTsBySku = getLatestTimestampBySku(po.raw_inward_qc_sku_variants);
  const sentTsBySku = getLatestTimestampBySku(po.print_outward_sku_variants);
  const qcSkus = new Set(
    (po.raw_inward_qc_sku_variants || [])
      .filter((variant) => Boolean(variant.sku))
      .map((variant) => normalizeSku(variant.sku)),
  );

  if (orderedMixes.length > 0 || receivedBySku.size > 0) {
    const skus = new Set<string>([
      ...orderedMixes.map((mix) => normalizeSku(mix.sku)),
      ...Array.from(receivedBySku.keys()),
    ]);

    let hasApproved = false;
    const allCovered = Array.from(skus).every((sku) => {
      const received = receivedBySku.get(sku) ?? 0;
      if (received <= 0) return true;

      // QC must exist and not be older than inward.
      if (!qcSkus.has(sku)) return false;

      // Tracked QC: only passed qty is sendable; pending already checked above.
      const passed = passedBySku.get(sku);
      if (passed != null) {
        if (passed <= 0) return true;
        hasApproved = true;
        return (sentBySku.get(sku) ?? 0) >= passed;
      }

      if (
        !isTimestampFreshOrUnknown(qcTsBySku.get(sku), inwardTsBySku.get(sku))
      ) {
        return false;
      }

      const approved = getNetInwardQty(received, rejectedBySku.get(sku) ?? 0);
      if (approved <= 0) return true;
      hasApproved = true;

      const sent = sentBySku.get(sku) ?? 0;
      if (sent < approved) return false;

      // Print outward must not be older than the QC that approved this qty.
      return isTimestampFreshOrUnknown(
        sentTsBySku.get(sku),
        qcTsBySku.get(sku),
      );
    });

    return hasApproved && allCovered;
  }

  if (rawQc.tracked) {
    const passed = rawQc.passed ?? 0;
    return passed > 0 && (po.print_outward_quantity ?? 0) >= passed;
  }

  const approved = getNetInwardQty(
    po.raw_inward_quantity ?? 0,
    po.raw_inward_rejection_quantity ?? 0,
  );
  if (approved <= 0) return false;
  if ((po.print_outward_quantity ?? 0) < approved) return false;

  // Fresh raw QC required after additional raw inward (e.g. print-reject replacements).
  return isTimestampFreshOrUnknown(
    toTimestamp(po.raw_inward_qc_modified_date),
    toTimestamp(po.raw_inward_modified_date),
  );
};

/**
 * Print inward complete when every sent SKU is fully inwarded,
 * and that inward is not older than the print outward for the same SKU.
 */
const isPrintInwardComplete = (
  po: InventoryOpsPoDetail,
  sizeMixes: InventoryOpsSizeMix[] | null | undefined,
): boolean => {
  const sentBySku = getQtyBySku(po.print_outward_sku_variants, getSentQty);
  const receivedBySku = getQtyBySku(
    po.print_inward_sku_variants,
    getReceivedQty,
  );
  const sentTsBySku = getLatestTimestampBySku(po.print_outward_sku_variants);
  const inwardTsBySku = getLatestTimestampBySku(po.print_inward_sku_variants);

  const sentEntries = Array.from(sentBySku.entries()).filter(
    ([, qty]) => qty > 0,
  );

  if (sentEntries.length > 0) {
    return sentEntries.every(([sku, sent]) => {
      if ((receivedBySku.get(sku) ?? 0) < sent) return false;
      return isTimestampFreshOrUnknown(
        inwardTsBySku.get(sku),
        sentTsBySku.get(sku),
      );
    });
  }

  // Product-level fallback when print outward has no SKU rows.
  const sent = po.print_outward_quantity ?? 0;
  if (sent <= 0) return false;
  // Same net-good rule as raw: received − rejected must cover what was sent.
  if (
    getNetInwardQty(
      po.print_inward_quantity ?? 0,
      po.print_inward_rejection_quantity ?? 0,
    ) < sent
  ) {
    return false;
  }

  // Fresh print inward required after additional print outward.
  return isTimestampFreshOrUnknown(
    toTimestamp(po.print_inward_modified_date),
    toTimestamp(po.print_outward_modified_date),
  );
};

/** Remaining raw inward qty when inwarding against the product (no variants). */
export const getProductRawInwardRemainingQty = (
  po: InventoryOpsPoDetail | null | undefined,
  options?: { customisationRequired?: boolean },
): number => {
  if (!po) return 0;
  const ordered = po.product_po_quantity ?? 0;
  const rawNet = getNetInwardQty(
    po.raw_inward_quantity ?? 0,
    po.raw_inward_rejection_quantity ?? 0,
  );
  // Print QC rejects must be replaced starting again from raw inward.
  const printRejected =
    options?.customisationRequired === false
      ? 0
      : (po.print_inward_rejection_quantity ?? 0);
  const effectiveTowardOrder = Math.max(0, rawNet - printRejected);
  return Math.max(0, ordered - effectiveTowardOrder);
};

/**
 * Remaining print outward qty when sending against the product (no variants).
 * Tracked raw QC caps at passed − sent; legacy rows at received − rejected − sent.
 */
export const getProductPrintOutwardRemainingQty = (
  po: InventoryOpsPoDetail | null | undefined,
): number => {
  if (!po) return 0;
  const rawQc = getQcSnapshot(po, "raw");
  if (rawQc.tracked) {
    return Math.max(0, (rawQc.passed ?? 0) - (po.print_outward_quantity ?? 0));
  }
  const approved = getNetInwardQty(
    po.raw_inward_quantity ?? 0,
    po.raw_inward_rejection_quantity ?? 0,
  );
  const sent = po.print_outward_quantity ?? 0;
  // Do not allow sending newly inwarded qty until 1st QC is fresher than raw inward.
  const qcFresh = isTimestampFreshOrUnknown(
    toTimestamp(po.raw_inward_qc_modified_date),
    toTimestamp(po.raw_inward_modified_date),
  );
  if (!qcFresh) return 0;
  return Math.max(0, approved - sent);
};

/** Remaining print inward qty when inwarding against the product (no variants). */
export const getProductPrintInwardRemainingQty = (
  po: InventoryOpsPoDetail | null | undefined,
): number => {
  if (!po) return 0;
  const sent = po.print_outward_quantity ?? 0;
  const net = getNetInwardQty(
    po.print_inward_quantity ?? 0,
    po.print_inward_rejection_quantity ?? 0,
  );
  return Math.max(0, sent - net);
};

export const isInventoryOpsPoTaskCompleted = (
  task: InventoryOpsPoTaskKey,
  po: InventoryOpsPoDetail | null | undefined,
  sizeMixes?: InventoryOpsSizeMix[] | null,
  options?: { customisationRequired?: boolean },
): boolean => {
  if (!po) return false;

  const customisationRequired = options?.customisationRequired !== false;
  const isPrintTask =
    task === "print_outward" ||
    task === "print_inward" ||
    task === "print_inward_qc";

  // Print steps are not required when the product has no customisation.
  if (isPrintTask && !customisationRequired) {
    return true;
  }

  switch (task) {
    case "raw_inward":
      return isNetInwardCompleteAgainstOrdered(
        po,
        sizeMixes,
        po.raw_inward_sku_variants,
        po.raw_inward_qc_sku_variants,
        po.raw_inward_quantity ?? 0,
        po.raw_inward_rejection_quantity ?? 0,
        {
          customisationRequired,
          printRejectedVariants: po.print_inward_qc_sku_variants,
          aggregatePrintRejected: po.print_inward_rejection_quantity ?? 0,
        },
      );

    case "raw_inward_qc":
      if (!po.raw_inward_id) return false;
      return isInwardQcComplete(
        po,
        sizeMixes,
        po.raw_inward_sku_variants,
        po.raw_inward_qc_sku_variants,
        po.raw_inward_quantity ?? 0,
        po.raw_inward_rejection_quantity,
        {
          aggregateInwardModifiedAt: po.raw_inward_modified_date,
          aggregateQcModifiedAt: po.raw_inward_qc_modified_date,
          trackedPendingQty: getQcSnapshot(po, "raw").pending,
        },
      );

    case "print_outward":
      return isPrintOutwardComplete(po, sizeMixes);

    case "print_inward":
      return isPrintInwardComplete(po, sizeMixes);

    case "print_inward_qc":
      if (!po.print_inward_id) return false;
      return isInwardQcComplete(
        po,
        sizeMixes,
        po.print_inward_sku_variants,
        po.print_inward_qc_sku_variants,
        po.print_inward_quantity ?? 0,
        po.print_inward_rejection_quantity,
        {
          aggregateInwardModifiedAt: po.print_inward_modified_date,
          aggregateQcModifiedAt: po.print_inward_qc_modified_date,
          trackedPendingQty: getQcSnapshot(po, "print").pending,
        },
      );

    default:
      return false;
  }
};
